import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { FREE_MODELS, MODEL_PROVIDERS } from "@/lib/constants/app";
import { cn } from "@/lib/utils";
import { sortModelsWithPinnedFirst } from "@/lib/utils/ai/modelUtils";
import useTempChatStore from "@/store/tempChatStore";
import { useState } from "react";
import { TbPinFilled } from "react-icons/tb";
import { useTouchDevice } from "@/hooks/useTouchDevice";

// Import provider icons
import { ClaudeIcon } from "@/components/Chat/header/icons/ClaudeIcon";
import { CohereIcon } from "@/components/Chat/header/icons/CohereIcon";
import { DeepSeekIcon } from "@/components/Chat/header/icons/DeepSeekIcon";
import { GeminiIcon } from "@/components/Chat/header/icons/GeminiIcon";
import { GrokIcon } from "@/components/Chat/header/icons/GrokIcon";
import { GroqIcon } from "@/components/Chat/header/icons/GroqIcon";
import { MistralIcon } from "@/components/Chat/header/icons/MistralIcon";
import { OpenAIIcon } from "@/components/Chat/header/icons/OpenAIIcon";
import { PerplexityIcon } from "@/components/Chat/header/icons/PerplexityIcon";
import { QwenIcon } from "@/components/Chat/header/icons/Qwen";

// Popular models pinned to the top of the selector
const PINNED_MODELS = [
  "gpt-5.2",
  "gpt-4.1-nano",
  "gemini-3-pro",
  "claude-sonnet-4-5-20250929",
  "deepseek-chat",
  "sonar",
];

// Model capabilities for tooltips
const MODEL_CAPABILITIES = {
  // OpenAI Models
  "gpt-4.1": "Great for quick coding and analysis",
  "gpt-4.1-mini": "Fast and efficient for lightweight tasks",
  "gpt-4.1-nano": "Ultra-fast responses for simple tasks",
  "gpt-4o": "Great for most tasks",
  "gpt-4o-mini": "Fast and cost-effective for most tasks",
  "gpt-5": "Advanced reasoning and complex problem-solving",
  "gpt-5-mini": "Balanced performance with improved speed",
  "gpt-5-nano": "Lightweight model for quick responses",
  "gpt-5.1": "Enhanced capabilities for coding and analysis",
  "gpt-5.1-codex-mini": "Specialized for code generation and debugging",
  "gpt-5.1-codex": "Advanced code generation and software development",
  "gpt-5.2": "Next-generation reasoning with enhanced multimodal capabilities",
  // Claude Models
  "claude-opus-4-20250514": "Most powerful model for complex reasoning",
  "claude-sonnet-4-20250514": "High-performance with exceptional reasoning",
  "claude-sonnet-4-5-20250929": "Smart model for complex agents and coding",
  "claude-3-7-sonnet-20250219": "High intelligence with extended thinking",
  "claude-3-5-haiku-20241022": "Quick, intelligent responses",
  // Gemini Models
  "gemini-2.5-pro": "Advanced reasoning and coding",
  "gemini-2.5-flash": "Hybrid reasoning with controllable thinking",
  "gemini-2.5-flash-lite": "Fast and cost-efficient multimodal model",
  "gemini-3-pro": "Richest multimodal understanding",
  // Mistral Models
  "mistral-small-latest": "State-of-the-art multimodal understanding",
  "open-mistral-nemo": "Multilingual capabilities and efficient reasoning",
  // DeepSeek Models
  "deepseek-chat": "Cost-effective with competitive coding",
  "deepseek-reasoner": "Transparent chain-of-thought reasoning",
  // Groq Models
  "llama-3.3-70b-versatile": "Latest Llama 3.3 with enhanced versatility",
  "moonshotai/kimi-k2-instruct-0905": "Kimi K2 instruction-tuned model",
  // XAI Models
  "grok-4-0709": "Next-generation reasoning",
  "grok-3": "Superior reasoning with advanced mathematics",
  "grok-code-fast-1": "Speedy reasoning for agentic coding",
  "grok-3-fast": "Low latency for time-sensitive applications",
  "grok-3-mini": "Cost-efficient reasoning for STEM tasks",
  "grok-3-mini-fast": "Rapid response for real-time reasoning",
  // Cohere Models
  "command-a-03-2025": "Exceptional tool use and RAG",
  "command-r7b-12-2024": "High throughput and low latency",
  "command-r-plus-08-2024": "Advanced multi-step tool use",
  "command-r-08-2024": "High precision on RAG and tool use",
  // Perplexity Models
  sonar: "Real-time web search with speed optimization",
  "sonar-pro": "Deep content understanding with 2x more citations",
  // Default
  default: "General purpose AI assistant",
};

/**
 * Get provider icon component based on provider name
 */
const getProviderIcon = (provider) => {
  switch (provider) {
    case "deepseek":
      return (
        <DeepSeekIcon
          size={14}
          className="text-[#015482] dark:text-[#015482]"
        />
      );
    case "gemini":
      return (
        <GeminiIcon size={14} className="text-[#1967D2] dark:text-[#1967D2]" />
      );
    case "mistral":
      return (
        <MistralIcon
          size={14}
          className="text-orange-600 dark:text-orange-400"
        />
      );
    case "anthropic":
      return <ClaudeIcon size={14} className="fill-[#D97757]" />;
    case "groq":
      return (
        <GroqIcon size={14} className="text-green-600 dark:text-green-400" />
      );
    case "xai":
      return (
        <GrokIcon size={14} className="text-blue-400 dark:text-blue-200" />
      );
    case "cohere":
      return (
        <CohereIcon size={14} className="text-teal-600 dark:text-teal-400" />
      );
    case "perplexity":
      return (
        <PerplexityIcon size={14} className="text-red-600 dark:text-red-400" />
      );
    case "qwen":
      return (
        <QwenIcon size={14} className="text-purple-600 dark:text-purple-400" />
      );
    case "openai":
    default:
      return (
        <OpenAIIcon
          size={14}
          className="fill-black dark:fill-white text-black dark:text-white"
        />
      );
  }
};

/**
 * Determine provider from model ID
 */
const determineProvider = (model) => {
  if (!model) return "openai";
  return MODEL_PROVIDERS[model] || "openai";
};

/**
 * Format model name for display
 */
const formatModelName = (modelName) => {
  // Display name mappings
  const displayNameMap = {
    "moonshotai/kimi-k2-instruct-0905": "kimi-k2-instruct",
  };
  if (displayNameMap[modelName]) {
    return displayNameMap[modelName];
  }

  let cleanName = modelName;

  // Handle command models: command-a-03-2025 -> command-a
  cleanName = cleanName.replace(/(command-[a-z0-9]+)-\d{2}-\d{4}$/, "$1");

  // Remove date patterns like -20250219, -20241022, etc. (YYYYMMDD format)
  cleanName = cleanName.replace(/-\d{8}$/, "");

  // Remove other timestamp patterns
  cleanName = cleanName.replace(/-\d{2}-\d{4}$/, "");

  return cleanName;
};

/**
 * Icon wrapper component
 */
const IconWrapper = ({ provider }) => {
  return (
    <div
      className={cn(
        "w-5 h-5 rounded-full flex items-center justify-center",
        "bg-transparent",
        "border border-gray-300 dark:border-gray-500"
      )}
    >
      {getProviderIcon(provider)}
    </div>
  );
};

/**
 * TempChatModelSelector - Simplified model selector for temp chat
 * Shows FREE_MODELS only, no authentication required
 */
export function TempChatModelSelector({ className }) {
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  const selectedModel = useTempChatStore((state) => state.selectedModel);
  const setSelectedModel = useTempChatStore((state) => state.setSelectedModel);

  // Detect touch devices to use native select for better scroll experience
  const { isTouchDevice } = useTouchDevice();

  // Build model options from FREE_MODELS, with pinned models first
  const allOptions = FREE_MODELS.map((modelId) => ({
    value: modelId,
    label: modelId,
    provider: MODEL_PROVIDERS[modelId] || "openai",
  }));
  const sortedOptions = sortModelsWithPinnedFirst(allOptions, PINNED_MODELS);
  const pinnedSet = new Set(PINNED_MODELS);
  const pinnedOptions = sortedOptions.filter((o) => pinnedSet.has(o.value));
  const unpinnedOptions = sortedOptions.filter((o) => !pinnedSet.has(o.value));

  const handleModelChange = (newValue) => {
    const provider = MODEL_PROVIDERS[newValue] || "openai";
    setSelectedModel(newValue, provider);
  };

  const currentProvider = determineProvider(selectedModel);

  // Native select handler for touch devices
  const handleNativeSelectChange = (e) => {
    const newValue = e.target.value;
    if (newValue) {
      handleModelChange(newValue);
    }
  };

  // Render native select for touch devices (iOS/Android)
  // This provides much better UX as it uses the native picker wheel
  if (isTouchDevice) {
    return (
      <div className={cn("relative", className)}>
        <div className="flex items-center gap-1.5 h-8 px-2">
          <IconWrapper provider={currentProvider} />
          <select
            value={selectedModel}
            onChange={handleNativeSelectChange}
            className={cn(
              "appearance-none bg-transparent",
              "text-sm font-medium",
              "text-foreground",
              "border-none outline-none",
              "cursor-pointer",
              "pr-4",
              "min-w-0 max-w-[140px]",
              "truncate"
            )}
            style={{
              WebkitAppearance: "none",
              MozAppearance: "none",
            }}
          >
            {sortedOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {formatModelName(option.label)}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  // Desktop: Use Radix UI Select with full styling
  return (
    <Select
      value={selectedModel}
      onValueChange={handleModelChange}
      open={isSelectOpen}
      onOpenChange={setIsSelectOpen}
    >
      <SelectTrigger
        className={cn(
          "h-8 w-auto min-w-[140px] max-w-[200px] px-2 rounded-md",
          "bg-transparent",
          "border border-transparent",
          "hover:bg-zinc-100/50 dark:hover:bg-zinc-800/50",
          "transition-all duration-200",
          "focus:outline-none focus:ring-0 focus-visible:ring-0",
          "text-sm font-medium",
          "[&>svg]:hidden", // Hide the default chevron icon
          className
        )}
      >
        <div className="flex items-center gap-1.5">
          <IconWrapper provider={currentProvider} />
          <SelectValue>
            <span className="truncate">{formatModelName(selectedModel)}</span>
          </SelectValue>
        </div>
      </SelectTrigger>
      <SelectContent
        className={cn(
          "bg-white/80 dark:bg-zinc-900/80",
          "backdrop-blur-md",
          "border border-border/50",
          "w-[280px]",
          "rounded-2xl",
          "z-[400]"
        )}
        position="popper"
        sideOffset={4}
      >
        {/* Use native overflow instead of ScrollArea for better mobile Safari touch support */}
        <div className="max-h-[300px] overflow-y-auto pr-2 scrollbar-thin">
          {pinnedOptions.map((option) => (
            <TooltipProvider key={option.value}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <SelectItem
                      value={option.value}
                      className={cn(
                        "text-foreground",
                        "flex items-center gap-2 w-full",
                        "data-[highlighted]:bg-zinc-100 dark:data-[highlighted]:bg-zinc-800",
                        "data-[state=checked]:bg-zinc-100 dark:data-[state=checked]:bg-zinc-800",
                        "data-[state=checked]:font-medium",
                        "focus:outline-none focus:ring-0"
                      )}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <IconWrapper provider={option.provider} />
                        <span>{formatModelName(option.label)}</span>
                        <TbPinFilled className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 ml-auto" />
                      </div>
                    </SelectItem>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs z-[500]">
                  <div className="space-y-1">
                    <p className="font-medium">
                      {formatModelName(option.label)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {MODEL_CAPABILITIES[option.value] ||
                        MODEL_CAPABILITIES["default"]}
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
          <div className="border-b border-border/50 my-1 mx-2" />
          {unpinnedOptions.map((option) => (
            <TooltipProvider key={option.value}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <SelectItem
                      value={option.value}
                      className={cn(
                        "text-foreground",
                        "flex items-center gap-2 w-full",
                        "data-[highlighted]:bg-zinc-100 dark:data-[highlighted]:bg-zinc-800",
                        "data-[state=checked]:bg-zinc-100 dark:data-[state=checked]:bg-zinc-800",
                        "data-[state=checked]:font-medium",
                        "focus:outline-none focus:ring-0"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <IconWrapper provider={option.provider} />
                        <span>{formatModelName(option.label)}</span>
                      </div>
                    </SelectItem>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs z-[500]">
                  <div className="space-y-1">
                    <p className="font-medium">
                      {formatModelName(option.label)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {MODEL_CAPABILITIES[option.value] ||
                        MODEL_CAPABILITIES["default"]}
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>
      </SelectContent>
    </Select>
  );
}

export default TempChatModelSelector;
